import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/hooks/use-toast";
import axios from "axios";
import { invalidateFamilyQueries } from "@/utils/queryCache";

const FAMILY_SERVICE_URL = import.meta.env.VITE_FAMILY_SERVICE_URL || "/api/v1";

export type FamilyMemberRole = "ADMIN" | "SPOUSE" | "CHILD" | "DEPENDENT";

export interface FamilyInvitation {
  id: string;
  family_id: string;
  families?: { name: string } | null;
  invited_email: string;
  relationship: string;
  role: FamilyMemberRole;
  invited_by: string;
  status: "pending" | "accepted" | "declined" | "expired" | "cancelled";
  token: string;
  expires_at: string;
  last_reminder_sent: string | null;
  reminder_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * Hook for managing family invitations
 */
export const useInvitations = (familyId?: string) => {
  const { user, session } = useAuth();
  const queryClient = useQueryClient();

  // Get authorization headers
  const getHeaders = () => {
    if (!session?.access_token) {
      throw new Error("No active session");
    }
    return {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
    };
  };

  /**
   * Fetch pending invitations for current user
   */
  const { data: pendingInvitations, isLoading: loadingPending } = useQuery({
    queryKey: ["invitations", "pending", user?.id],
    queryFn: async (): Promise<FamilyInvitation[]> => {
      if (!user?.id || !session?.access_token) return [];

      try {
        const headers = getHeaders();
        const response = await axios.get(`${FAMILY_SERVICE_URL}/invitations/pending`, {
          headers,
          timeout: 10000,
        });

        return response.data?.data || [];
      } catch (error) {
        console.warn("[useInvitations] Failed to fetch pending invitations:", error);
        // Return empty array on error to prevent infinite retries
        return [];
      }
    },
    enabled: !!user?.id && !!session?.access_token,
    retry: 1, // Only retry once
    retryDelay: 1000, // Wait 1 second before retry
    refetchInterval: 60000, // Refetch every minute
    staleTime: 30000, // Consider data stale after 30 seconds
  });

  /**
   * Fetch family invitations (admin only)
   */
  const { data: familyInvitations, isLoading: loadingFamily } = useQuery({
    queryKey: ["invitations", "family", familyId],
    queryFn: async (): Promise<FamilyInvitation[]> => {
      if (!familyId || !session?.access_token) return [];

      try {
        const headers = getHeaders();
        const response = await axios.get(`${FAMILY_SERVICE_URL}/families/${familyId}/invitations`, {
          headers,
          timeout: 10000,
        });

        return response.data?.data || [];
      } catch (error) {
        console.warn("[useInvitations] Failed to fetch family invitations:", error);
        // Return empty array on error to prevent infinite retries
        return [];
      }
    },
    enabled: !!familyId && !!user?.id && !!session?.access_token,
    retry: 1, // Only retry once
    retryDelay: 1000, // Wait 1 second before retry
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 15000,
  });

  /**
   * Send invitation mutation
   */
  const sendInvitationMutation = useMutation({
    mutationFn: async ({
      familyId,
      email,
      relationship,
      role,
    }: {
      familyId: string;
      email: string;
      relationship: string;
      role: FamilyMemberRole;
    }) => {
      const headers = getHeaders();
      const response = await axios.post(
        `${FAMILY_SERVICE_URL}/families/${familyId}/invite`,
        { email, relationship, role },
        { headers, timeout: 10000 }
      );

      return response.data;
    },
    onSuccess: (data, variables) => {
      const resent = Boolean(
        data &&
        typeof data === "object" &&
        "data" in data &&
        (data as { data?: { resent?: boolean } }).data?.resent
      );
      toast({
        title: resent ? "✅ Invitation Email Resent" : "✅ Invitation Sent Successfully",
        description: resent
          ? `We re-sent the pending invitation email to ${variables.email}. Ask them to check inbox and spam.`
          : `An invitation has been sent to ${variables.email}. They have 7 days to accept.`,
        variant: "default",
        className:
          "border-green-500 bg-green-50 dark:bg-green-950 text-green-900 dark:text-green-100",
      });

      // Invalidate family invitations query
      queryClient.invalidateQueries({ queryKey: ["invitations", "family", variables.familyId] });
    },
    onError: (error: unknown) => {
      let errorMessage = "Failed to send invitation";
      if (axios.isAxiosError<{ message?: string }>(error)) {
        errorMessage = error.response?.data?.message || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      // Custom styled error toast matching TellerWand theme
      if (errorMessage.includes("cannot invite yourself")) {
        toast({
          title: "👤 Self-Invitation Not Allowed",
          description:
            "You're already the admin of this family. You can invite other members instead.",
          variant: "default",
          className:
            "border-amber-500 bg-amber-50 dark:bg-amber-950 text-amber-900 dark:text-amber-100",
        });
      } else if (errorMessage.includes("already a member")) {
        toast({
          title: "👥 Member Already Exists",
          description:
            "This person is already part of your family. You can edit their role instead.",
          variant: "default",
          className: "border-blue-500 bg-blue-50 dark:bg-blue-950 text-blue-900 dark:text-blue-100",
        });
      } else if (
        errorMessage.includes("disposable") ||
        errorMessage.includes("not allowed for security")
      ) {
        toast({
          title: "🔒 Email Not Allowed",
          description: errorMessage,
          variant: "default",
          className: "border-red-500 bg-red-50 dark:bg-red-950 text-red-900 dark:text-red-100",
        });
      } else {
        toast({
          title: "❌ Cannot Send Invitation",
          description: errorMessage,
          variant: "default",
          className: "border-red-500 bg-red-50 dark:bg-red-950 text-red-900 dark:text-red-100",
        });
      }
    },
  });

  /**
   * Accept invitation mutation
   */
  const acceptInvitationMutation = useMutation({
    mutationFn: async (token: string) => {
      const headers = getHeaders();
      const response = await axios.post(
        `${FAMILY_SERVICE_URL}/invitations/accept`,
        { token },
        { headers, timeout: 10000 }
      );

      return response.data;
    },
    onSuccess: () => {
      toast({
        title: "Invitation Accepted",
        description: "You have successfully joined the family!",
      });

      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["invitations", "pending"] });
      invalidateFamilyQueries(queryClient, user?.id);
    },
    onError: (error: unknown) => {
      let errorMessage = "Failed to accept invitation";
      if (axios.isAxiosError<{ message?: string }>(error)) {
        errorMessage = error.response?.data?.message || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast({
        title: "Cannot Accept Invitation",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  /**
   * Decline invitation mutation
   */
  const declineInvitationMutation = useMutation({
    mutationFn: async (token: string) => {
      const headers = getHeaders();
      const response = await axios.post(
        `${FAMILY_SERVICE_URL}/invitations/decline`,
        { token },
        { headers, timeout: 10000 }
      );

      return response.data;
    },
    onSuccess: () => {
      toast({
        title: "Invitation Declined",
        description: "You have declined the invitation.",
      });

      // Invalidate pending invitations query
      queryClient.invalidateQueries({ queryKey: ["invitations", "pending"] });
    },
    onError: (error: unknown) => {
      let errorMessage = "Failed to decline invitation";
      if (axios.isAxiosError<{ message?: string }>(error)) {
        errorMessage = error.response?.data?.message || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast({
        title: "Cannot Decline Invitation",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  /**
   * Cancel invitation mutation (admin only)
   */
  const cancelInvitationMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const headers = getHeaders();
      const response = await axios.delete(`${FAMILY_SERVICE_URL}/invitations/${invitationId}`, {
        headers,
        timeout: 10000,
      });

      return response.data;
    },
    onSuccess: () => {
      toast({
        title: "Invitation Cancelled",
        description: "The invitation has been cancelled successfully.",
      });

      // Invalidate family invitations query
      queryClient.invalidateQueries({ queryKey: ["invitations", "family"] });
    },
    onError: (error: unknown) => {
      let errorMessage = "Failed to cancel invitation";
      if (axios.isAxiosError<{ message?: string }>(error)) {
        errorMessage = error.response?.data?.message || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast({
        title: "Cannot Cancel Invitation",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  /**
   * Send reminder mutation (admin only)
   */
  const sendReminderMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const headers = getHeaders();
      const response = await axios.post(
        `${FAMILY_SERVICE_URL}/invitations/${invitationId}/remind`,
        {},
        { headers, timeout: 10000 }
      );
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: "Reminder Sent",
        description: "A reminder email has been sent for this invitation.",
      });
      queryClient.invalidateQueries({ queryKey: ["invitations", "family"] });
    },
    onError: (error: unknown) => {
      let errorMessage = "Failed to send reminder";
      if (axios.isAxiosError<{ message?: string }>(error)) {
        errorMessage = error.response?.data?.message || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast({
        title: "Cannot Send Reminder",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  return {
    // Data
    pendingInvitations: pendingInvitations || [],
    familyInvitations: familyInvitations || [],

    // Loading states
    loadingPending,
    loadingFamily,

    // Mutations
    sendInvitation: (data: {
      familyId: string;
      email: string;
      relationship: string;
      role: FamilyMemberRole;
    }) => sendInvitationMutation.mutateAsync(data),
    acceptInvitation: (token: string) => acceptInvitationMutation.mutateAsync(token),
    declineInvitation: (token: string) => declineInvitationMutation.mutateAsync(token),
    cancelInvitation: (invitationId: string) => cancelInvitationMutation.mutateAsync(invitationId),
    sendReminder: (invitationId: string) => sendReminderMutation.mutateAsync(invitationId),

    // Mutation states
    isSending: sendInvitationMutation.isPending,
    isAccepting: acceptInvitationMutation.isPending,
    isDeclining: declineInvitationMutation.isPending,
    isCancelling: cancelInvitationMutation.isPending,
    isReminding: sendReminderMutation.isPending,
  };
};
